import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EventService from '../../services/EventService';
import Header from '../../core/Header';
import QrScanner from 'react-qr-scanner';

function EventCheckinWeb() {
    const { id: eventId } = useParams();
    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState(null);
    const [scannerEnabled, setScannerEnabled] = useState(false);
    const [changedTickets, setChangedTickets] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [processingScan, setProcessingScan] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const loadEventData = async () => {
            try {
                const response = await EventService.getEventCheckIn(eventId);
                setEvent(response.data);
            } catch (error) {
                setError('Failed to load tickets');
            } finally {
                setLoading(false);
            }
        };
        loadEventData();
    }, [eventId]);

    const handleTicketCheckboxChange = (ticket) => {
        const updatedTickets = event.tickets.map(t => {
            if (t.ticketId === ticket.ticketId) {
                return { ...t, scanned: !t.scanned };
            }
            return t;
        });
        setEvent({ ...event, tickets: updatedTickets });

        const updatedChangedTickets = [...changedTickets];
        const index = updatedChangedTickets.findIndex(t => t.ticketId === ticket.ticketId);

        if (index > -1) {
            updatedChangedTickets[index].scanned = !updatedChangedTickets[index].scanned;
        } else {
            updatedChangedTickets.push({ ticketId: ticket.ticketId, scanned: !ticket.scanned });
        }

        setChangedTickets(updatedChangedTickets);
    };

    const submitTickets = async () => {
        setSubmitting(true);
        try {
            await EventService.submitChangedTickets(changedTickets);
            setChangedTickets([]);
        } catch (error) {
            setError('Failed to submit tickets');
        } finally {
            setSubmitting(false);
        }
    };

    const submitScannedTicket = async (ticketUrl) => {
        const ticketId = (ticketUrl || '').split('/').pop();
        if (!ticketId) {
            return;
        }
        setSubmitting(true);
        setProcessingScan(true);
        try {
            const response = await EventService.scanTicket(ticketId, eventId);
            setError('');

            let message = 'Ticket scanned successfully';
            let info = response && response.data ? response.data : null;
            if (!info && event && Array.isArray(event.tickets)) {
                const localTicket = event.tickets.find(t => t.ticketId === ticketId);
                if (localTicket) {
                    const email = getTicketEmail(localTicket) || localTicket.ticketName || '';
                    const paidText = typeof localTicket.paid === 'boolean' ? (localTicket.paid ? 'Paid' : 'Unpaid') : '';
                    message = `Scanned: ${email}${paidText ? ` • ${paidText}` : ''}`;
                }
            } else if (info) {
                const email = info.mail || '';
                const name = info.name || '';
                const paidText = typeof info.paid === 'boolean' ? (info.paid ? 'Paid' : 'Unpaid') : '';
                const label = email || name;
                if (label || paidText) {
                    message = `Scanned: ${label}${paidText ? ` • ${paidText}` : ''}`;
                }
            }
            setSuccess(message);

            setEvent((prev) => {
                if (!prev || !prev.tickets) return prev;
                const wasUnscanned = prev.tickets.some(t => t.ticketId === ticketId && !t.scanned);
                const updatedTickets = prev.tickets.map(t =>
                    t.ticketId === ticketId ? { ...t, scanned: true } : t
                );
                return {
                    ...prev,
                    tickets: updatedTickets,
                    ticketsChecked: typeof prev.ticketsChecked === 'number' && wasUnscanned
                        ? prev.ticketsChecked + 1
                        : prev.ticketsChecked,
                };
            });

            setChangedTickets((prev) => {
                const idx = prev.findIndex(t => t.ticketId === ticketId);
                if (idx > -1) {
                    const clone = [...prev];
                    clone[idx] = { ...clone[idx], scanned: true };
                    return clone;
                }
                return [...prev, { ticketId, scanned: true }];
            });
        } catch (error) {
            setError('Failed to scan ticket');
            setSuccess('');
        } finally {
            setSubmitting(false);
            setProcessingScan(false);
        }
    };

    const toggleScanner = () => {
        setScannerEnabled((prev) => !prev);
    };

    const handleScan = (data) => {
        if (!data || processingScan) return;
        const text = typeof data === 'string' ? data : (data.text || data.data || '');
        if (!text) return;
        submitScannedTicket(text);
    };

    const handleError = (err) => {
        setError('Scanner error');
        console.error(err);
    };

    const getTicketEmail = (ticket) => {
        return (
            ticket.email ||
            ticket.ticketName ||
            ticket.customerMail ||
            ticket.mail ||
            ticket.userEmail ||
            ''
        );
    };

    const sortedTickets = event?.tickets
        ? [...event.tickets].sort((a, b) => {
            const emailA = getTicketEmail(a).toLowerCase().trim();
            const emailB = getTicketEmail(b).toLowerCase().trim();
            // Put empty emails at the end
            if (!emailA && !emailB) return 0;
            if (!emailA) return 1;
            if (!emailB) return -1;
            // Simple alphabetical comparison
            if (emailA < emailB) return -1;
            if (emailA > emailB) return 1;
            return 0;
        })
        : [];

    return (
        <div className="bg-gray-50 min-h-screen">
            <Header isLoggedIn={true} logoutUser={() => console.log('Logged out')} />
            <div className="container mx-auto py-8">
                {loading ? (
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-gray-700">
                            <span className="spinner-border spinner-border-sm mr-2"></span>
                            Loading Event...
                        </h3>
                    </div>
                ) : event ? (
                    <div className="flex flex-col md:flex-row bg-white rounded-lg shadow-lg overflow-hidden">
                        {error && (
                            <div className="text-red-500 text-center p-4">
                                {error}
                            </div>
                        )}
                        {success && !error && (
                            <div className="text-green-600 text-center p-4">
                                {success}
                            </div>
                        )}

                        <div className="w-full p-6">
                            <h2 className="text-3xl font-bold text-gray-800 mb-4">{event.eventName}</h2>
                            <p className="text-lg font-medium text-gray-700 mb-2">
                                Tickets sold: {event.ticketsSold}
                            </p>
                            <p className="text-lg font-medium text-gray-700 mb-4">
                                Tickets scanned: {event.ticketsChecked}
                            </p>

                            <button
                                className="bg-blue-500 text-white rounded-md px-4 py-2 hover:bg-blue-600 transition-colors duration-200 mb-6"
                                onClick={toggleScanner}
                            >
                                {scannerEnabled ? 'Disable Scanner' : 'Enable Scanner'}
                            </button>

                            {scannerEnabled && (
                                <div className="mb-6">
                                    <QrScanner
                                        delay={300}
                                        onError={handleError}
                                        onScan={handleScan}
                                        style={{ width: '100%' }}
                                        constraints={{ video: { facingMode: 'environment' } }}
                                    />
                                </div>
                            )}

                            <div className="mb-8">
                                <h4 className="text-xl font-semibold mb-2">Paid Tickets</h4>
                                <table className="table-auto w-full mb-4">
                                    <thead>
                                    <tr>
                                        <th className="px-4 py-2 text-left">Ticket Name</th>
                                        <th className="px-4 py-2 text-left">Scanned</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {sortedTickets
                                        .filter((ticket) => ticket.paid)
                                        .map((ticket) => (
                                            <tr key={ticket.ticketId}>
                                                <td className="px-4 py-2">{ticket.ticketName}</td>
                                                <td className="px-4 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={ticket.scanned}
                                                        onChange={() => handleTicketCheckboxChange(ticket)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="mb-8">
                                <h4 className="text-xl font-semibold mb-2">Unpaid Tickets</h4>
                                <table className="table-auto w-full mb-4">
                                    <thead>
                                    <tr>
                                        <th className="px-4 py-2 text-left">Ticket Name</th>
                                        <th className="px-4 py-2 text-left">Scanned</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {sortedTickets
                                        .filter((ticket) => !ticket.paid)
                                        .map((ticket) => (
                                            <tr key={ticket.ticketId}>
                                                <td className="px-4 py-2">{ticket.ticketName}</td>
                                                <td className="px-4 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={ticket.scanned}
                                                        onChange={() => handleTicketCheckboxChange(ticket)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <button
                                className="bg-green-600 text-white rounded-md px-4 py-2 hover:bg-green-700 transition-colors duration-200"
                                onClick={submitTickets}
                                disabled={submitting}
                            >
                                {submitting ? 'Submitting...' : 'Submit Scans'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <h3 className="text-xl font-bold text-center">No event data available</h3>
                )}
            </div>
        </div>
    );
}

export default EventCheckinWeb;


